/**
 * Qo'ng'iroq orkestratori — P2P, keyin kerak bo'lsa LiveKit (D-035, D-036).
 *
 * Almashish SHAFFOF: `call` ekrani qaysi yo'l ishlayotganini bilmaydi,
 * u faqat `state` ni ko'radi. `transport` esa telemetriyaga ketadi —
 * M-001 metrikasi aynan shundan yig'iladi.
 *
 * Fallback ikki sababdan boshlanadi:
 *   1. P2P `failed` holatiga tushdi (ICE muvaffaqiyatsiz)
 *   2. `rtc.p2p_timeout_ms` ichida ulanmadi (configdan)
 *
 * Ikkinchisi kerak, chunki ICE ba'zan `failed` bermay osilib qoladi.
 */

import type { WsEvent } from '@voice/api-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { request } from '../api/client';
import { useRealtimeEvents } from '../realtime/context';
import { P2PSession, isOfferer } from './p2p';
import { isMicError, type VoiceSession, type VoiceState } from './index';

interface CallOptions {
  matchId: number;
  myUserId: number;
  peerUserId: number;
  stunUrls: string[];
  p2pTimeoutMs: number;
  onPeerLeft: () => void;
}

export interface CallController {
  state: VoiceState;
  muted: boolean;
  toggleMute: () => void;
  hangUp: () => Promise<void>;
  /** Suhbatdoshning ovozi — `<audio>` elementiga ulanadi. */
  remoteStream: MediaStream | null;
}

export function useVoiceCall(options: CallOptions): CallController {
  const [state, setState] = useState<VoiceState>('connecting');
  const [muted, setMuted] = useState(false);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  const session = useRef<VoiceSession | null>(null);
  const p2p = useRef<P2PSession | null>(null);
  const fallbackStarted = useRef(false);
  const ended = useRef(false);
  // `options` har renderda yangi obyekt — effekt uni bog'liqlikka
  // olsa qo'ng'iroq har renderda qaytadan qurilardi.
  //
  // Yangilash effektda, render paytida emas: render toza bo'lishi kerak.
  // Bu effekt quyidagilardan OLDIN e'lon qilingan, ya'ni mount'da
  // birinchi bo'lib ishlaydi va ulanish boshlanganda qiymat joyida.
  const config = useRef(options);
  useEffect(() => {
    config.current = options;
  });

  /** Telemetriya M-001 uchun — qaysi yo'l ishlaganini SERVER biladi. */
  const reportTelemetry = useCallback(async () => {
    const active = session.current;
    if (!active) return;
    try {
      await request(`/match/${config.current.matchId}/telemetry`, {
        method: 'POST',
        body: {
          transport: active.transport,
          ice_ms: active.iceMs,
          reconnects: active.reconnects,
        },
      });
    } catch {
      // Telemetriya yo'qolsa qo'ng'iroq buzilmaydi — u metrikaga kerak,
      // foydalanuvchiga emas.
    }
  }, []);

  const switchToLiveKit = useCallback(async () => {
    if (fallbackStarted.current || ended.current) return;
    fallbackStarted.current = true;

    await p2p.current?.stop();
    p2p.current = null;

    try {
      // ── LiveKit SHU YERDA yuklanadi, ilova ochilganda emas ──
      //
      // `livekit-client` bundle'ning eng katta bo'lagi (~500 kB), lekin u
      // FALLBACK: P2P ishlagan foydalanuvchi uni umuman ko'rmaydi.
      // Statik import bo'lganda hamma uni yuklardi — shu jumladan
      // hech qachon qo'ng'iroq qilmaganlar ham.
      //
      // Bu to'lov konversiyasiga to'g'ridan-to'g'ri tegadi: sekin
      // ochiladigan Mini App'da odam to'lov ekraniga yetib bormaydi.
      const { LiveKitSession } = await import('./livekit');

      const livekit = new LiveKitSession(config.current.matchId, {
        onState: setState,
        onRemoteStream: setRemoteStream,
      });
      session.current = livekit;

      await livekit.start();
    } catch (err) {
      // Mikrofon rad etilgan bo'lsa LiveKit ham yordam bermaydi —
      // u yerda ham mikrofon kerak.
      setState(isMicError(err) ? 'no-mic' : 'failed');
    }
    // Telemetriya HAR HOLDA: ulanish yarmida uzilgan bo'lsa ham
    // "livekit urinildi" degan fakt M-001 uchun qimmatli.
    await reportTelemetry();
  }, [reportTelemetry]);

  // ── Ulanishni boshlash ──
  useEffect(() => {
    const { matchId, myUserId, peerUserId, stunUrls, p2pTimeoutMs } = config.current;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const rtc = new P2PSession({
      matchId,
      isOfferer: isOfferer(myUserId, peerUserId),
      stunUrls,
      handlers: {
        onState: (next) => {
          if (next === 'failed') {
            void switchToLiveKit();
            return;
          }
          if (next === 'connected' && timeout) {
            clearTimeout(timeout);
            timeout = null;
            void reportTelemetry();
          }
          setState(next);
        },
        onRemoteStream: setRemoteStream,
      },
    });
    p2p.current = rtc;
    session.current = rtc;

    // ICE ba'zan `failed` bermay osilib qoladi — muddat shu holat uchun.
    timeout = setTimeout(() => void switchToLiveKit(), p2pTimeoutMs);

    void rtc.start().catch((err: unknown) => {
      if (isMicError(err)) {
        // Fallback BOSHLANMAYDI: muammo tarmoqda emas, ruxsatda.
        if (timeout) clearTimeout(timeout);
        setState('no-mic');
        return;
      }
      void switchToLiveKit();
    });

    return () => {
      if (timeout) clearTimeout(timeout);
      void session.current?.stop();
      session.current = null;
      p2p.current = null;
    };
  }, [switchToLiveKit, reportTelemetry]);

  // ── WS signallari ──
  useRealtimeEvents(
    useCallback((event: WsEvent) => {
      if (event.type === 'rtc.signal' && event.match_id === config.current.matchId) {
        void p2p.current?.handleSignal(event.kind, event.payload);
      } else if (event.type === 'call.ended' && event.match_id === config.current.matchId) {
        ended.current = true;
        config.current.onPeerLeft();
      }
    }, []),
  );

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      session.current?.setMuted(next);
      return next;
    });
  }, []);

  const hangUp = useCallback(async () => {
    ended.current = true;
    await session.current?.stop();
    try {
      await request(`/match/${config.current.matchId}/end`, {
        method: 'POST',
        body: { reason: 'user' },
      });
    } catch {
      // Server allaqachon yopgan bo'lishi mumkin — idempotent.
    }
  }, []);

  return { state, muted, toggleMute, hangUp, remoteStream };
}
